import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { FieldLabel } from '../../components/ui/FieldLabel';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  ProductResource,
  ProductResourceType,
  ProductResourceUpsertPayload,
  createProductResource,
  deleteProductResource,
  listProductResources,
  updateProductResource,
} from '../../services/productResourcesApi';
import { productResourceFieldHelp } from './productResourceFieldHelp';

const RESOURCE_TYPE_OPTIONS: { value: ProductResourceType; label: string }[] = [
  { value: 'Document', label: 'Documento' },
  { value: 'Template', label: 'Template' },
  { value: 'Video', label: 'Vídeo' },
  { value: 'Link', label: 'Link' },
  { value: 'Integration', label: 'Integração' },
  { value: 'Other', label: 'Outro' },
];

type ProductResourceFormState = Omit<ProductResourceUpsertPayload, 'sortOrder'>;

const emptyForm: ProductResourceFormState = {
  name: '',
  description: '',
  resourceType: 'Link',
  url: '',
  isActive: true,
};

function resourceTypeLabel(value: string): string {
  return RESOURCE_TYPE_OPTIONS.find((item) => item.value === value)?.label ?? value;
}

export function ProductResourcesPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('sales.product-resources');
  const [items, setItems] = useState<ProductResource[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ProductResourceFormState>(emptyForm);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();
  const [saving, setSaving] = useState(false);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listProductResources({
        page: targetPage,
        pageSize,
        search: search || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar recursos.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(resource: ProductResource): void {
    expandForm();
    setEditingId(resource.id);
    setForm({
      name: resource.name,
      description: resource.description,
      resourceType: resource.resourceType,
      url: resource.url ?? '',
      isActive: resource.isActive,
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const editingItem = editingId ? items.find((item) => item.id === editingId) : null;
    const payload: ProductResourceUpsertPayload = {
      ...form,
      sortOrder: editingItem?.sortOrder ?? 0,
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateProductResource(editingId, payload);
        toast.success('Recurso atualizado.');
        resetForm();
      } else {
        await createProductResource(payload);
        toast.success('Recurso criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao salvar recurso.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(resource: ProductResource): Promise<void> {
    if (!canWrite) return;

    const accepted = await confirm({
      title: 'Excluir recurso',
      message: `Deseja excluir "${resource.name}"? Produtos vinculados perderão este vínculo.`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteProductResource(resource.id);
      toast.success('Recurso excluído.');
      if (editingId === resource.id) {
        collapseForm(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir recurso.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Recursos de produto"
        description="Materiais e deliverables reutilizáveis — documentos, links, templates e integrações."
        actions={
          <Link to="/sales/products" className="erp-link-back">
            ← Voltar para produtos
          </Link>
        }
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar recurso' : 'Novo recurso'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId ? `Editando ${form.name || 'recurso selecionado'} — clique para expandir` : undefined
          }
          createSummary="Clique para expandir e cadastrar um novo recurso"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
          <div className="erp-form-grid">
            <label className="erp-label">
              <FieldLabel {...productResourceFieldHelp.name} required>
                Nome
              </FieldLabel>
              <input
                className="erp-input"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
              />
            </label>
            <label className="erp-label">
              <FieldLabel {...productResourceFieldHelp.resourceType} required>
                Tipo
              </FieldLabel>
              <select
                className="erp-input"
                value={form.resourceType ?? 'Link'}
                onChange={(event) =>
                  setForm({ ...form, resourceType: event.target.value as ProductResourceType })
                }
              >
                {RESOURCE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="erp-label mt-3 block">
            <FieldLabel {...productResourceFieldHelp.url}>URL (opcional)</FieldLabel>
            <input
              type="url"
              className="erp-input mt-1"
              value={form.url ?? ''}
              onChange={(event) => setForm({ ...form, url: event.target.value })}
              placeholder="https://..."
            />
          </label>
          <label className="erp-label mt-3 block">
            <FieldLabel {...productResourceFieldHelp.description}>Descrição</FieldLabel>
            <textarea
              className="erp-input mt-1 min-h-[60px]"
              value={form.description ?? ''}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </label>
          <label className="erp-label-inline mt-3">
            <input
              type="checkbox"
              checked={form.isActive ?? true}
              onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
            />
            Ativo
          </label>
          <FormActionBar
            onCancel={() => collapseForm(resetForm)}
            cancelLabel="Cancelar"
            className="erp-btn-actions mt-4"
          >
            <button type="submit" disabled={saving} className="erp-btn-primary">
              {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
            </button>
          </FormActionBar>
        </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <input
          className="erp-input max-w-md"
          placeholder="Buscar por nome..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void loadData(1);
            }
          }}
        />
        <button type="button" className="erp-btn-secondary" onClick={() => void loadData(1)}>
          Buscar
        </button>
      </div>

      {loading ? (
        <LoadingSpinner label="Carregando recursos..." />
      ) : (
        <div className="erp-card overflow-x-auto">
          <table className="erp-table w-full">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                <th>URL</th>
                <th>Status</th>
                {canWrite ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={canWrite ? 5 : 4} className="text-center text-secondary-500">
                    Nenhum recurso cadastrado.
                  </td>
                </tr>
              ) : (
                items.map((resource) => (
                  <tr key={resource.id}>
                    <td>{resource.name}</td>
                    <td>{resourceTypeLabel(resource.resourceType)}</td>
                    <td className="max-w-xs truncate text-sm">
                      {resource.url ? (
                        <a href={resource.url} target="_blank" rel="noreferrer" className="erp-link">
                          {resource.url}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <ActiveStatusBadge active={resource.isActive} />
                    </td>
                    {canWrite ? (
                      <td className="text-right">
                        <EditIconButton onClick={() => startEdit(resource)} label="Editar" />
                        <DeleteIconButton onClick={() => void handleDelete(resource)} label="Excluir" />
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <ListPaginationBar
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPrevious={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
      />
    </PageShell>
  );
}
